import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  Box,
  FormControl,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Slide
} from "@mui/material";

import {
  getDeliveryBoys,
  assignBatch,
  assignSingleOrder
} from "../../api/adminApi";

// Modern input styling matching the application's aesthetic
const modernInputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#FFFFFF',
    '& fieldset': {
      borderColor: '#ECE5DA',
    },
    '&:hover fieldset': {
      borderColor: '#F29D17',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#F8C332',
      borderWidth: '2px'
    },
    '&.Mui-disabled': {
      bgcolor: '#FFFDF8',
    }
  }
};

// Smooth slide animation coming down from the top
function SlideTransition(props) {
  return <Slide {...props} direction="down" />;
}

export default function AssignModal({
  open,
  setOpen,
  batchId,
  orderId,
  singleOrder = false,
  onSuccess
}) {
  const [boys, setBoys] = useState([]);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(false);

  // Custom Toast/Snackbar State
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (open) {
      fetchBoys();
    }
  }, [open]);

  const fetchBoys = async () => {
    try {
      const res = await getDeliveryBoys();
      setBoys(
        res.data.filter(
          (b) => b.status === "IDLE"
        )
      );
    } catch (err) {
      console.error("Error fetching delivery boys:", err);
    }
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast((prev) => ({ ...prev, open: false }));
  };

  const handleAssign = async () => {
    if (!selected) {
      setToast({ open: true, message: "Please select a delivery boy", severity: "warning" });
      return;
    }

    setLoading(true);

    try {
      if (singleOrder) {
        await assignSingleOrder({
          order_id: orderId,
          delivery_boy_id: selected
        });
      } else {
        await assignBatch({
          batch_id: batchId,
          delivery_boy_id: selected
        });
      }

      // Show Custom Success Toast
      setToast({ open: true, message: "✅ Assigned successfully!", severity: "success" });

      // 👇 FIXED: Immediately close modal and trigger data refresh so the Assign button vanishes instantly
      setOpen(false);
      setSelected("");
      if (onSuccess) onSuccess();

    } catch (err) {
      console.error(err);
      // Show Custom Error Toast
      setToast({
        open: true,
        message: err.response?.data?.error || "Assignment failed (check delivery boy status)",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => !loading && setOpen(false)}
        maxWidth="xs"
        fullWidth
        fullScreen={fullScreenDialog}
        PaperProps={{
          sx: {
            borderRadius: fullScreenDialog ? 0 : '24px',
            bgcolor: '#FFFDF8',
            boxShadow: '0 24px 48px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: "900", color: '#3A2C23', borderBottom: "1px dashed #ECE5DA", pb: 2, pt: 3, px: { xs: 2.5, sm: 4 }, textAlign: 'center' }}>
          Assign Delivery
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2.5, sm: 4 }, pt: "24px !important" }}>

          <Box sx={{ bgcolor: "#FFFFFF", p: 2.5, borderRadius: '16px', border: "1px solid #ECE5DA", boxShadow: '0 4px 12px rgba(0,0,0,0.03)', textAlign: 'center', mb: 3 }}>
            <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
              ASSIGNING {singleOrder ? "ORDER" : "BATCH"}
            </Typography>
            <Typography variant="h5" fontWeight="900" sx={{ color: '#3A2C23' }}>
              #{singleOrder ? orderId : batchId}
            </Typography>
          </Box>

          <FormControl fullWidth sx={modernInputStyle}>
            <Select
              displayEmpty
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiSelect-select': {
                  color: selected ? '#3A2C23' : '#A59D96',
                  fontWeight: '800'
                }
              }}
            >
              <MenuItem value="" disabled sx={{ fontWeight: 800, color: '#A59D96' }}>
                Select Delivery Boy
              </MenuItem>

              {boys.length === 0 && (
                <MenuItem disabled sx={{ fontWeight: 800 }}>
                  No idle delivery boys found
                </MenuItem>
              )}

              {boys.map((b) => (
                <MenuItem key={b.id} value={b.id} sx={{ fontWeight: 800, color: '#3A2C23' }}>
                  #{b.id} - {b.name} <Typography component="span" sx={{ color: '#577A34', fontWeight: 900, ml: 1 }}>({b.status})</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2.5, sm: 3 }, px: { sm: 4 }, borderTop: "1px solid #ECE5DA", bgcolor: '#FFFFFF', borderRadius: '0 0 24px 24px', justifyContent: 'center', gap: 1.5 }}>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            disabled={loading}
            sx={{
              borderRadius: '12px',
              fontWeight: 800,
              color: '#6E655E',
              borderColor: '#ECE5DA',
              textTransform: 'none',
              flex: 1,
              py: 1.2,
              '&:hover': { bgcolor: '#FFFDF8', borderColor: '#A59D96' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={loading || !selected}
            disableElevation
            sx={{
              textTransform: "none",
              fontWeight: 900,
              borderRadius: '12px',
              bgcolor: '#F8C332',
              color: '#3A2C23',
              flex: 1,
              py: 1.2,
              boxShadow: (loading || !selected) ? 'none' : '0 4px 12px rgba(248, 195, 50, 0.3)',
              '&:hover': { bgcolor: '#F5B71D' },
              '&.Mui-disabled': { bgcolor: '#FFF6DE', color: '#A59D96' }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: '#3A2C23' }} /> : "Assign Delivery"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modern Customized Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Changed to show at the top
        TransitionComponent={SlideTransition}
        sx={{ zIndex: 10000, mt: 2 }} // Added a small margin top (mt: 2) so it doesn't touch the absolute top edge
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: '12px',
            fontWeight: '700',
            fontSize: '1rem',
            boxShadow: '0px 8px 24px rgba(0,0,0,0.2)',
            // Customizing colors to match your theme optionally
            ...(toast.severity === 'success' && {
              bgcolor: '#577A34',
              color: '#FFFFFF'
            })
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}